(() => {
  'use strict';

  const installationKey = Symbol.for('buddhatrace.epicHeader');
  if (window[installationKey]) {
    window[installationKey].init(document);
    return;
  }

  const instances = new Map();
  const rootSelector = '[data-bt-epic-header]';
  const focusableSelector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  class EpicHeader {
    constructor(root) {
      this.root = root;
      this.bar = root.querySelector('[data-bt-eh-bar]');
      this.toggle = root.querySelector('[data-bt-eh-toggle]');
      this.panel = root.querySelector('[data-bt-eh-panel]');
      this.bag = root.querySelector('[data-bt-eh-bag]');
      this.preview = root.querySelector('[data-bt-eh-preview]');
      this.caption = root.querySelector('[data-bt-eh-caption-target]');
      this.events = new AbortController();
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.isOpen = false;
      this.lastY = window.scrollY;
      this.scrollFrame = 0;
      this.openFrame = 0;
      this.hideTimer = 0;
      this.focusTimer = 0;
      this.previewTimer = 0;
      this.cartTimer = 0;
      this.cartSequence = 0;
      this.cartRequest = null;
      this.unsubscribeCart = null;
      this.pendingImage = this.preview ? this.preview.src : '';
      this.goodImage = this.preview ? this.preview.src : '';
      this.goodCaption = this.caption ? this.caption.textContent : '';

      const signal = this.events.signal;
      this.toggle.addEventListener('click', () => this.setOpen(!this.isOpen, true), {signal});
      root.addEventListener('click', (event) => {
        if (this.isOpen && event.target.closest('a[href]')) this.setOpen(false, false);
      }, {signal});
      root.addEventListener('pointerover', (event) => this.previewLink(event), {signal});
      root.addEventListener('focusin', (event) => this.previewLink(event), {signal});
      document.addEventListener('keydown', (event) => this.onKeydown(event), {signal});
      window.addEventListener('scroll', () => this.queueScroll(), {passive: true, signal});
      window.addEventListener('resize', () => {
        this.measure();
        this.queueScroll();
      }, {passive: true, signal});
      window.addEventListener('focus', () => this.queueCartRefresh(), {signal});
      window.addEventListener('pageshow', () => {
        this.lastY = window.scrollY;
        this.queueScroll();
        this.queueCartRefresh();
      }, {signal});
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') this.queueCartRefresh();
      }, {signal});
      ['cart:updated', 'cart:refresh', 'cart:change'].forEach((name) => {
        document.addEventListener(name, () => this.queueCartRefresh(), {signal});
      });
      window.addEventListener('load', () => this.subscribeToCart(), {once: true, signal});

      if (this.preview) {
        this.preview.addEventListener('load', () => this.previewLoaded(), {signal});
        this.preview.addEventListener('error', () => this.previewFailed(), {signal});
      }
      if (this.bag) {
        // Dawn replaces this anchor's children after cart mutations. Keep the anchor
        // and its native drawer event handlers intact, and requery its new children.
        this.bagObserver = new MutationObserver(() => {
          ++this.cartSequence;
          if (this.cartRequest) this.cartRequest.abort();
          this.updateBagAccessibility();
          this.queueCartRefresh();
        });
        this.bagObserver.observe(this.bag, {childList: true});
      }
      if (typeof ResizeObserver === 'function') {
        this.resizeObserver = new ResizeObserver(() => this.measure());
        this.resizeObserver.observe(this.bar);
      }

      root.dataset.btEhReady = 'true';
      this.measure();
      this.applyScrollState(window.scrollY, 0);
      this.updateBagAccessibility();
      this.subscribeToCart();
      this.queueCartRefresh();
    }

    measure() {
      const height = this.bar.getBoundingClientRect().height;
      if (height > 0) this.root.style.setProperty('--bt-eh-height', height + 'px');
      if (this.isOpen) this.setMenuOrigin();
    }

    setMenuOrigin() {
      const bounds = this.toggle.getBoundingClientRect();
      this.root.style.setProperty('--bt-eh-origin-x', (bounds.left + 14) + 'px');
      this.root.style.setProperty('--bt-eh-origin-y', (bounds.top + bounds.height / 2) + 'px');
    }

    setOpen(open, returnFocus) {
      if (this.isOpen === open) return;
      this.isOpen = open;
      clearTimeout(this.hideTimer);
      clearTimeout(this.focusTimer);
      cancelAnimationFrame(this.openFrame);
      this.toggle.setAttribute('aria-expanded', String(open));
      this.root.classList.remove('is-hidden');

      if (open) {
        this.setMenuOrigin();
        this.root.setAttribute('role', 'dialog');
        this.root.setAttribute('aria-modal', 'true');
        this.root.setAttribute('aria-label', 'Site menu');
        this.panel.hidden = false;
        this.panel.inert = false;
        this.panel.removeAttribute('inert');
        this.panel.setAttribute('aria-hidden', 'false');
        document.body.classList.add('bt-eh-menu-lock');
        // Commit the closed clip-path before starting the circular reveal.
        this.panel.getBoundingClientRect();
        this.openFrame = requestAnimationFrame(() => {
          this.root.classList.add('is-open');
          this.openFrame = 0;
        });
        this.focusTimer = setTimeout(() => {
          if (this.isOpen && document.activeElement === this.toggle) {
            const firstLink = this.panel.querySelector('a[href]');
            if (firstLink) firstLink.focus({preventScroll: true});
          }
        }, this.motion.matches ? 0 : 500);
      } else {
        this.root.classList.remove('is-open');
        this.root.removeAttribute('role');
        this.root.removeAttribute('aria-modal');
        this.root.removeAttribute('aria-label');
        this.panel.inert = true;
        this.panel.setAttribute('inert', '');
        this.panel.setAttribute('aria-hidden', 'true');
        if (!document.querySelector(rootSelector + '.is-open')) {
          document.body.classList.remove('bt-eh-menu-lock');
        }
        this.hideTimer = setTimeout(() => {
          if (!this.isOpen) this.panel.hidden = true;
        }, this.motion.matches ? 0 : 1050);
        if (returnFocus && this.toggle.isConnected) this.toggle.focus({preventScroll: true});
      }
    }

    focusableElements() {
      return Array.from(this.root.querySelectorAll(focusableSelector)).filter((element) => {
        return !element.closest('[hidden], [inert]') &&
          element.getAttribute('aria-hidden') !== 'true' && element.getClientRects().length > 0;
      });
    }

    onKeydown(event) {
      if (!this.isOpen) return;
      const bag = event.target.closest && event.target.closest('[data-bt-eh-bag]');
      if (bag && this.root.contains(bag) &&
        (event.key === 'Enter' || event.key === ' ' || event.code === 'Space')) {
        // Dawn's drawer handles Space on keydown without dispatching a click.
        // Release our modal before its own focus trap takes over.
        this.setOpen(false, false);
        return;
      }
      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        this.setOpen(false, true);
        return;
      }
      if (event.key !== 'Tab') return;
      const elements = this.focusableElements();
      const first = elements[0] || this.toggle;
      const last = elements[elements.length - 1] || this.toggle;
      if (!this.root.contains(document.activeElement)) {
        event.preventDefault();
        (event.shiftKey ? last : first).focus({preventScroll: true});
      } else if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus({preventScroll: true});
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus({preventScroll: true});
      }
    }

    queueScroll() {
      if (this.scrollFrame) return;
      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = 0;
        const y = window.scrollY;
        this.applyScrollState(y, y - this.lastY);
        this.lastY = y;
      });
    }

    applyScrollState(y, delta) {
      this.root.classList.toggle('is-solid', y > 30);
      if (this.isOpen) this.root.classList.remove('is-hidden');
      else if (delta > 4 && y > window.innerHeight * .7) this.root.classList.add('is-hidden');
      else if (delta < -4 || y < window.innerHeight * .7) this.root.classList.remove('is-hidden');
    }

    previewLink(event) {
      if (!this.preview || !this.caption || !event.target.closest) return;
      const link = event.target.closest('[data-bt-eh-image]');
      if (!link || !this.root.contains(link)) return;
      const src = new URL(link.dataset.btEhImage, document.baseURI).href;
      const caption = link.dataset.btEhCaption || '';
      this.caption.textContent = caption;
      if (this.pendingImage === src) return;
      clearTimeout(this.previewTimer);
      this.pendingImage = src;
      this.preview.style.opacity = '0';
      this.previewTimer = setTimeout(() => {
        this.previewTimer = 0;
        this.preview.src = src;
        if (this.preview.complete && this.preview.naturalWidth) this.previewLoaded();
      }, this.motion.matches ? 0 : 200);
    }

    previewLoaded() {
      const current = this.preview.currentSrc || this.preview.src;
      if (current !== this.pendingImage || this.previewTimer) return;
      this.goodImage = current;
      this.goodCaption = this.caption ? this.caption.textContent : '';
      this.preview.style.opacity = '1';
    }

    previewFailed() {
      if ((this.preview.currentSrc || this.preview.src) !== this.pendingImage) return;
      if (this.goodImage && this.preview.src !== this.goodImage) {
        this.pendingImage = this.goodImage;
        this.preview.src = this.goodImage;
        if (this.caption) this.caption.textContent = this.goodCaption;
      }
      this.preview.style.opacity = '1';
    }

    updateBagAccessibility(count) {
      const bag = this.root.querySelector('[data-bt-eh-bag]');
      if (!bag) return;
      const visible = bag.querySelector('[data-bt-eh-count]');
      if (count === undefined && visible) count = Number(visible.textContent.trim());
      if (!Number.isFinite(count) || count < 0) return;
      if (visible) {
        visible.textContent = String(count);
        visible.classList.toggle('has-items', count > 0);
      }
      const description = count === 1 ? '1 item' : count + ' items';
      const screenReader = bag.querySelector('[data-bt-eh-count-label]');
      if (screenReader) screenReader.textContent = description + ' in your bag';
      bag.setAttribute('aria-label', 'Open your bag, ' + description);
    }

    queueCartRefresh() {
      clearTimeout(this.cartTimer);
      this.cartTimer = setTimeout(() => this.refreshCart(), 120);
    }

    async refreshCart() {
      if (!this.root.isConnected) return;
      const sequence = ++this.cartSequence;
      if (this.cartRequest) this.cartRequest.abort();
      const request = new AbortController();
      this.cartRequest = request;
      const base = this.root.dataset.rootUrl || '/';
      const url = base.replace(/\/?$/, '/') + 'cart.js';
      try {
        const response = await fetch(url, {
          credentials: 'same-origin',
          cache: 'no-store',
          headers: {'Accept': 'application/json'},
          signal: request.signal
        });
        if (!response.ok) return;
        const cart = await response.json();
        if (sequence !== this.cartSequence || request.signal.aborted || !this.root.isConnected) return;
        if (typeof cart.item_count === 'number') this.updateBagAccessibility(cart.item_count);
      } catch (_) {
        // Keep the last server-confirmed count if the refresh is unavailable.
      } finally {
        if (this.cartRequest === request) this.cartRequest = null;
      }
    }

    subscribeToCart() {
      if (this.unsubscribeCart) return;
      try {
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
          this.unsubscribeCart = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.queueCartRefresh());
        }
      } catch (_) {
        // App-provided carts can use the document cart events instead.
      }
    }

    destroy() {
      this.setOpen(false, false);
      this.events.abort();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.bagObserver) this.bagObserver.disconnect();
      if (typeof this.unsubscribeCart === 'function') this.unsubscribeCart();
      if (this.cartRequest) this.cartRequest.abort();
      cancelAnimationFrame(this.scrollFrame);
      cancelAnimationFrame(this.openFrame);
      [this.hideTimer, this.focusTimer, this.previewTimer, this.cartTimer].forEach(clearTimeout);
      this.panel.hidden = true;
      this.root.removeAttribute('data-bt-eh-ready');
    }
  }

  function init(container) {
    const roots = [];
    if (container.matches && container.matches(rootSelector)) roots.push(container);
    if (container.querySelectorAll) roots.push(...container.querySelectorAll(rootSelector));
    roots.forEach((root) => {
      if (instances.has(root) || root.dataset.btEhReady === 'true') return;
      if (!root.querySelector('[data-bt-eh-bar]') || !root.querySelector('[data-bt-eh-toggle]') || !root.querySelector('[data-bt-eh-panel]')) return;
      instances.set(root, new EpicHeader(root));
    });
  }

  window[installationKey] = {init};
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), {once: true});
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
  document.addEventListener('shopify:section:unload', (event) => {
    instances.forEach((instance, root) => {
      if (event.target === root || event.target.contains(root)) {
        instance.destroy();
        instances.delete(root);
      }
    });
  });
})();
